import { ActivityApplicationConstants } from "@/apps/tatar/activityApp/ActivityHooks";
import FormValidators from "@/components/Form/Validation/FormValidators";
import i18n from "@/i18n";
import BFRadioGroup from "@/modules/abstract-ui/forms/radio-group/BFRadioGroup";
import BfRadio from "@/modules/abstract-ui/forms/radio/BfRadio";
import BFMessage from "@/modules/abstract-ui/general/Message/BFMessage";
import EZGroup from "@/modules/ez-form/form-group/ez-form/EZGroup";
import CacheService from "@/services/CacheService";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { Field } from "react-final-form";
import {
  VacancyApplicant,
  VacancyApplication,
} from "../../../model/VacancyApplicant.interface";
import VacancyOfferUtils from "../../utils/VacancyOfferUtils";

type Props = {
  constants: ActivityApplicationConstants;
  editable?: boolean;
  onChanged?: () => void;
  noPadding?: boolean;
  application: VacancyApplication;
  applicant: VacancyApplicant;
  renderGuarantor?: boolean;
};

const VacancyApplicantCreditReportPart = (props: Props) => {
  const {
    application,
    applicant,
    constants,
    editable = false,
    onChanged,
    noPadding = false,
    renderGuarantor = false,
  } = props;

  const [localApplication, setLocalApplication] =
    useState<VacancyApplication>(application);

  // Update the local applicant if the prop changes.
  useEffect(() => {
    setLocalApplication(application);
  }, [application]);

  const data = renderGuarantor ? applicant.guarantor : applicant;

  const applicantIndex = application.data.applicants?.findIndex(
    (a) => a.id === applicant.id
  );

  const allowanceText = data.agreeCreditCheck
    ? i18n.t(
        "apTemplate:Vacancy.Applicant.Label.CreditWorthiness.Allowance.Permitted",
        "Zustimmung zum Abrufen der Schufa Daten gegeben"
      )
    : i18n.t(
        "apTemplate:Vacancy.Applicant.Label.CreditWorthiness.Allowance.NotPermitted",
        "Keine Zustimmung zum Abrufen der Schufa Daten"
      );

  return (
    <div
      className={classNames("vacancy-applicant-credit-report-part", {
        "padding-20": !noPadding,
      })}
    >
      <div className="__h3">
        {i18n.t(
          "apTemplate:Vacancy.Applicant.Label.CreditWorthiness.Title",
          "Bonität"
        )}
      </div>

      <EZGroup
        readonly={!editable}
        overwriteHttpMethod="patch"
        value={localApplication}
        url={`/${constants?.serviceUrl}/applicant/${localApplication._id}`}
        onSubmitSuccess={(value) => {
          CacheService.updateDataInCaches(localApplication._id, value);
          setLocalApplication(value as VacancyApplication);

          if (onChanged) {
            onChanged();
          }
        }}
        transformSubmitData={(value) => {
          return { data: { ...value.data } };
        }}
      >
        {(renderProps) => {
          if (renderGuarantor) {
            return (
              <CreditWorthinessField
                name={`data.applicants[${applicantIndex}].guarantor.creditWorthiness`}
                editable={editable}
              />
            );
          }
          return (
            <CreditWorthinessField
              name={`data.applicants[${applicantIndex}].creditWorthiness`}
              editable={editable}
            />
          );
        }}
      </EZGroup>

      <div className="padding-top-20 __width-100">
        <BFMessage
          iconSize="lg"
          showIcon
          type={`${data.agreeCreditCheck ? "success" : "warning"}`}
          text={allowanceText}
        />
      </div>
    </div>
  );
};

export default VacancyApplicantCreditReportPart;

const CreditWorthinessField = (props: { editable?: boolean; name: string }) => {
  const { editable = false, name } = props;

  return (
    <Field
      name={name}
      validate={FormValidators.compose(
        FormValidators.max(100),
        FormValidators.min(0)
      )}
    >
      {({ input, meta }) => (
        <>
          {editable ? (
            <BFRadioGroup {...input}>
              {VacancyOfferUtils.getCreditWorthinessOptions().map((option) => {
                return (
                  <BfRadio key={option.value} value={option.value}>
                    {option.label}
                  </BfRadio>
                );
              })}
            </BFRadioGroup>
          ) : (
            <div className="__sub-text">
              {VacancyOfferUtils.getCreditWorthinessLabel(input.value)}
            </div>
          )}
        </>
      )}
    </Field>
  );
};
